import { CSSProperties, useCallback, useState, useEffect } from "react";
import useCampaignBuilder, { CampaignExistsState, CURRENT_BUDGET_TABS } from "Hooks/useCampaignBuilder";
import 'rc-slider/assets/index.css';
import { clsx } from "modules/Utils";
import TabsSymphony, { TabItem } from "elements/TabsSymphony";
import ButtonSymphony from "elements/ButtonSymphony";
import InputSymphony from "elements/InputSymphony";
import dayjs from "dayjs";
import { AdPlatform, CURRENCY_SYMBOLS, DEFAULT_CURRENCY, MINIMUM_DAILY_BUDGET_PER_DAY_IN_DOLLARS, PLAYBOOK_TYPE_INFO } from "modules/Const";
import useFacebookConnections from "Hooks/useFacebookConnections";
import { getConvertedCurrency_api } from "api/campaignApi";
import { CurrencyCodesType } from "modules/Types";
import debounce from 'lodash/debounce';
import CardSymphony from "elements/CardSymphony";
import Banner from "elements/Banner";
import Sty from "modules/Sty";
import useBrand, { BrandExistsState } from "Hooks/useBrand";
import SliderSymphony from "elements/SliderSymphony";

const BUDGET_TABS = [
    {
        id: CURRENT_BUDGET_TABS.LIFETIME,
        title: 'Lifetime Budget'
    },
    // {
    //     id: CURRENT_BUDGET_TABS.DAILY,
    //     title: 'Daily Budget'
    // },
];

/** Minimum daily budget requirement */
const MINIMUM_DAILY_BUDGET = 10;

/** Maps actual budget values to equidistant positions on slider for lifetime budget */
const lifetimeBudgetValueMap = {
    100: 100,    // First point (250-500 clicks)
    250: 250,    // First point (250-500 clicks)
    500: 500,    // Second point (1,250-2,500 clicks)
    1000: 1000,  // Third point (2,500-5,000 clicks)
};

/** Maps actual budget values to equidistant positions on slider for daily budget */
const dailyBudgetValueMap = {
    10: 10,     // First point (25-50 clicks/day)
    25: 33,     // Second point (62-125 clicks/day)
    50: 66,     // Third point (125-250 clicks/day)
    100: 100    // Last point (250-500 clicks/day)
};

/** Budget mark configurations for the lifetime budget slider */
const lifetimeBudgetMarks = [
    { value: 100, label: '$100', description: '250 to 500 clicks', placement: 'first' as const },
    { value: 250, label: '$250', description: '1,250 to 2,500 clicks', placement: 'middle' as const },
    { value: 500, label: '$500', description: '2,500 to 5,000 clicks', placement: 'middle' as const },
    { value: 1000, label: '$1,000', description: '5,000 to 10,000 clicks', placement: 'last' as const }
]

/** Budget mark configurations for the daily budget slider */
const dailyBudgetMarks = [
    { value: 10, label: '$10', description: '25 to 50 clicks/day', placement: 'first' as const },
    { value: 25, label: '$25', description: '62 to 125 clicks/day', placement: 'middle' as const },
    { value: 50, label: '$50', description: '125 to 250 clicks/day', placement: 'middle' as const },
    { value: 100, label: '$100', description: '250 to 500 clicks/day', placement: 'last' as const }
]

interface BudgetSelectorProps {
    className: string;
    style?: CSSProperties;
    title?: string;
    description?: string;
}

const BudgetSelector = ({
    className,
    style,
    title = 'Marketing Budget',
    description = 'Add a total budget for your campaign.',
}: BudgetSelectorProps) => {
    const {
        budget,
        setBudget,
        currentBudgetTab,
        setCurrentBudgetTab,
        startDate,
        endDate,
        numberOfDays,
        currentDateSelectionTab,
        checkIsAdBudgetTooLow,
        playbookType,
        isBudgetTooLow,
    } = useCampaignBuilder() as CampaignExistsState

    const adPlatform = PLAYBOOK_TYPE_INFO[playbookType].adPlatform;

    const {
        selectedFacebookAdAccount
    } = useFacebookConnections();

    const {
        currentBrand,
    } = useBrand() as BrandExistsState;

    // Add state for Facebook min budget
    const [facebookMinBudget, setFacebookMinBudget] = useState<number>(10);

    // Revalidate budget whenever relevant inputs change
    useEffect(() => {
        (async () => {
            await checkIsAdBudgetTooLow();
        })();
    }, [budget, startDate, endDate, numberOfDays, currentDateSelectionTab, selectedFacebookAdAccount, checkIsAdBudgetTooLow]);

    const renderMarks = (labelValue: string, description: string, placement: 'first' | 'middle' | 'last' = 'middle') => {
        const markStyle = { marginTop: 30 };

        let markPlacement: CSSProperties = { alignItems: 'center' }
        if (placement === 'first') {
            markPlacement = { alignItems: 'flex-start', marginLeft: 80 }
        } else if (placement === 'last') {
            markPlacement = { alignItems: 'flex-end', marginRight: 80 }
        }

        return {
            style: markStyle, label: (
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 120, ...markPlacement }}>
                    <div className="budgetSelectorMarkLabel">
                        {labelValue}
                    </div>
                    <div className="budgetSelectorMarkDescription">
                        {description}
                    </div>
                </div>
            )
        };
    }

    const getCurrencyToShow = () => {
        switch (adPlatform) {
            case AdPlatform.TIKTOK:
                return DEFAULT_CURRENCY;
            case AdPlatform.META:
                return selectedFacebookAdAccount?.currency;
            default:
                // NOTE: for other platforms, we use the brand's currency
                return currentBrand.currency?.code;
        }
    }

    const renderMaestroHelpMessage = () => {
        return null
        return (
            <ButtonSymphony
                className="maestroHelpMessage"
                variant="text"
                style={{ fontWeight: 400 }}
                iconLeft="↑"
            >
                Brands of your size tend to spend $500
            </ButtonSymphony>
        )
    }

    const renderFooterMessage = () => {
        return null

        return (
            <div className="footerMessage" style={sty.footerMessage}>
                Maestro helps you get more fans for your campaign.
            </div>
        )
    }

    const isValidBudget = async (value: string) => {
        let budgetLocal = Number(value);

        if (budgetLocal <= 0) {
            return false
        }

        const currencyFromFacebook = selectedFacebookAdAccount?.currency || DEFAULT_CURRENCY;

        // NOTE: if international, convert to USD, then check if daily budget meets minimum requirement
        if (currencyFromFacebook !== DEFAULT_CURRENCY) {
            const budgetPayload = {
                from: currencyFromFacebook as CurrencyCodesType,
                amount: MINIMUM_DAILY_BUDGET_PER_DAY_IN_DOLLARS,
                to: DEFAULT_CURRENCY as CurrencyCodesType
            }

            try {
                const convertedBudget = await getConvertedCurrency_api(budgetPayload);
                budgetLocal = convertedBudget
            } catch (error) {
                console.error(error);
                return false;
            }
        }

        const dayCount = dayjs(endDate).diff(dayjs(startDate), 'day');
        const dailyBudget = budgetLocal / dayCount;
        if (dailyBudget < MINIMUM_DAILY_BUDGET_PER_DAY_IN_DOLLARS) {
            return false;
        }

        return true;
    }
    /** Validates budget amount after user stops typing */
    const debouncedBudgetUpdate = debounce(async (value: string) => {
        // Validate the budget
        await checkIsAdBudgetTooLow();
    }, 300);

    /** Handles budget validation and updates */
    const handleBudgetChange = useCallback((value: string) => {
        // Update the input immediately for responsive typing
        setBudget(value);

        // Debounce the validation and network call
        debouncedBudgetUpdate(value);
    }, [setBudget, debouncedBudgetUpdate]);

    useEffect(() => {
        getFacebookMinBudget();
    }, [selectedFacebookAdAccount])

    const getFacebookMinBudget = async () => {
        if (!selectedFacebookAdAccount) {
            return
        }

        const isDefaultCurrency = selectedFacebookAdAccount?.currency === DEFAULT_CURRENCY;
        let minBudget = await getConvertedCurrency_api({ from: 'USD', to: selectedFacebookAdAccount?.currency as CurrencyCodesType, amount: 10 });
        minBudget = isDefaultCurrency ? 10 : Math.ceil(minBudget);
        setFacebookMinBudget(minBudget);
    }

    const renderBudgetTooLowInput = () => {
        // Use the local state instead of undefined isValid variable
        if (!isBudgetTooLow) {
            return null;
        }

        const isTikTokAds = adPlatform === AdPlatform.TIKTOK;

        const minBudget = isTikTokAds ? 50 : facebookMinBudget;
        let title = '';
        let description = '';

        if (isTikTokAds) {
            title = `Your budget is too low for effective ads.`
            description = `Please enter a budget of at least ${CURRENCY_SYMBOLS[getCurrencyToShow() as CurrencyCodesType] || '$'}${minBudget} total.`
        } else {
            if (currentBudgetTab === CURRENT_BUDGET_TABS.LIFETIME) {
                title = `Your lifetime budget is too low for effective ads.`
                description = `Please enter a lifetime budget that allows at least ${CURRENCY_SYMBOLS[getCurrencyToShow() as CurrencyCodesType] || '$'}${minBudget} per day.`
            } else {
                title = `Your budget is too low for effective ads.`
                description = `Please enter a budget of at least ${CURRENCY_SYMBOLS[getCurrencyToShow() as CurrencyCodesType] || '$'}${minBudget} per day.`
            }
        }

        return (
            <Banner
                className="budgetTooLowBanner"
                style={{ border: Sty.border }}
                title={title}
                color="yellow"
            >
                <div
                    className="budgetTooLowBannerDescription"
                    style={Sty.text16grey}
                >
                    {description}
                </div>
            </Banner>
        )
    }

    // NOTE: render()
    return (
        <CardSymphony
            className={clsx("budgetCampaignViewComponent", className)}
            style={style}
            title={title}
            subtitle={description}
            size="small"
            isPadding16
        >
            <div className="budgetInputCampaignBudgetSelector">
                This campaign should spend (in {CURRENCY_SYMBOLS[getCurrencyToShow() as CurrencyCodesType] || '$'}{getCurrencyToShow()}):
            </div>
            <InputSymphony
                className="budgetInputCampaign"
                type="text"
                value={budget}
                format="money"
                onChange={(value) => handleBudgetChange(value)}
            />

            {/* TODO MEGH: Bring back once we enable daily budget. */}
            {/* <TabsSymphony
                className="budgetCampaignTabs"
                tabs={BUDGET_TABS}
                selectedTab={currentBudgetTab}
                onChange={(tab) => {
                    const tabLocal = tab as TabItem;
                    setCurrentBudgetTab(tabLocal.id)
                }}
                type="dots"
            /> */}
            {renderBudgetTooLowInput()}
            {renderMaestroHelpMessage()}
            <SliderSymphony
                className="budgetSlider"
                sliderOffset={currentBudgetTab === CURRENT_BUDGET_TABS.DAILY ? 2 : 42}
                value={parseInt(budget)}
                min={currentBudgetTab === CURRENT_BUDGET_TABS.DAILY ? 5 : 100}
                max={currentBudgetTab === CURRENT_BUDGET_TABS.DAILY ? 100 : 2000}
                marks={currentBudgetTab === CURRENT_BUDGET_TABS.DAILY ? dailyBudgetMarks : lifetimeBudgetMarks}
                valueMap={currentBudgetTab === CURRENT_BUDGET_TABS.DAILY ? dailyBudgetValueMap : lifetimeBudgetValueMap}
                onChange={(value) => setBudget(value.toString())}
                equidistant={true}
            />
            {renderFooterMessage()}
        </CardSymphony>
    )
}

const sty: Record<string, CSSProperties> = {
    budgetInput: {
        padding: 8,
        border: `1px solid #ddd`,
        borderRadius: 8,
    },
    footerMessage: {
        paddingTop: 16,
    }
}

export default BudgetSelector;